/**
 * SmartImportStagingModal — Enterprise Staging Grid & Bulk Enrichment
 * APV ERP Standard — Rule 2 (Canonical Forms), Rule 4 (Solid Sticky Columns),
 * Rule 6 (Clean Lookups), Rule 8 (Anti Blind-Buttons), Rule 9 (Bulk Operations)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  Sparkles,
  Zap,
  Filter,
  Check,
  RotateCcw,
  Package,
  Layers,
  FileSpreadsheet,
  Download,
} from 'lucide-react';
import { trpcClient } from '@/lib/trpc';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { downloadExcelTemplate, type ExcelColumnDef } from '@/lib/excel.utils';
import {
  useProductTypeLookup,
  useMaterialLookup,
  useUnitLookup,
  type FKOption,
} from '@/hooks/useFKLookup';

export interface StagingItem {
  id: string;
  rowNumber: number;
  matchStatus: 'MATCHED_EXACT' | 'MATCHED_NEEDS_REVIEW' | 'NEW_VALID' | 'NEW_NEEDS_ENRICHMENT' | 'CONFLICT';
  statusReason?: string;
  existingProductId?: string | null;
  isNewProduct: boolean;
  productCode: string;
  isAutoGeneratedCode: boolean;
  productName: string;
  customerId?: string | null;
  customerCode?: string | null;
  typeId?: string | null;
  typeName?: string | null;
  materialId?: string | null;
  materialName?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  flatLength?: number | null;
  flatWidth?: number | null;
  unit: string;
  quantity?: number | null;
  unitPrice?: number | null;
  costPrice?: number | null;
  amount?: number | null;
  poReference?: string | null;
  soReference?: string | null;
  customerPo?: string | null;
  deliveryDate?: string | null;
  deliveryDeadline?: string | null;
  notes?: string | null;
  specs?: string | null;
  missingFields: string[];
  isValid: boolean;
}

export interface SmartImportStagingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rawItems: any[];
  customerId?: string;
  customerName?: string;
  fileName?: string;
  scope?: 'ORDER' | 'CATALOG';
  onSuccess: (result: {
    createdProducts: any[];
    orderDetails: any[];
    totalCount: number;
  }) => void;
}

const COMMON_UNITS = ['hộp', 'cái', 'túi', 'bộ', 'ram', 'tờ', 'cuộn', 'thùng', 'kg'];

export function SmartImportStagingModal({
  open,
  onOpenChange,
  rawItems,
  customerId,
  customerName,
  fileName,
  scope = 'ORDER',
  onSuccess,
}: SmartImportStagingModalProps) {
  const [isInspecting, setIsInspecting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [items, setItems] = useState<StagingItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'ALL' | 'MATCHED' | 'INVALID' | 'NEW_VALID' | 'CONFLICT'>('ALL');
  const [overwriteMasterData, setOverwriteMasterData] = useState(false);

  // Bulk fill inputs
  const [bulkTypeId, setBulkTypeId] = useState<string>('');
  const [bulkMaterialId, setBulkMaterialId] = useState<string>('');
  const [bulkUnit, setBulkUnit] = useState<string>('');

  // Lookups
  const { options: productTypeOptions } = useProductTypeLookup();
  const { options: materialOptions } = useMaterialLookup();
  const { options: unitOptions } = useUnitLookup();

  const combinedUnits = useMemo(() => {
    const set = new Set([...COMMON_UNITS, ...unitOptions.map((u) => u.label)]);
    return Array.from(set);
  }, [unitOptions]);

  // Call inspection on open with rawItems
  useEffect(() => {
    if (!open || !rawItems || rawItems.length === 0) return;

    let isMounted = true;
    setIsInspecting(true);

    const runInspection = async () => {
      try {
        const res = await trpcClient.products.inspectImportBatch.mutate({
          items: rawItems,
          customerId: customerId || undefined,
          scope,
        });

        if (isMounted) {
          setItems(res.items as StagingItem[]);
          setSelectedIds(new Set());
          // Auto switch to INVALID tab if there are items needing enrichment
          if (res.summary.newNeedsEnrichmentCount > 0) {
            setActiveTab('INVALID');
          } else {
            setActiveTab('ALL');
          }
        }
      } catch (err: any) {
        toast.error(`Lỗi phân tích dữ liệu: ${err?.message || 'Không thể đối soát sản phẩm'}`);
      } finally {
        if (isMounted) setIsInspecting(false);
      }
    };

    runInspection();

    return () => {
      isMounted = false;
    };
  }, [open, rawItems, customerId, scope]);

  // Recalculate row validity when an item is modified
  const revalidateItem = useCallback((item: StagingItem): StagingItem => {
    const missing: string[] = [];
    if (item.isNewProduct) {
      if (!item.typeId) missing.push('typeId');
      if (!item.materialId) missing.push('materialId');
      if ((!item.length || item.length === 0) && (!item.flatLength || item.flatLength === 0)) {
        missing.push('length');
      }
      if ((!item.width || item.width === 0) && (!item.flatWidth || item.flatWidth === 0)) {
        missing.push('width');
      }
      if (!item.unit) missing.push('unit');
      if (!item.productName?.trim()) missing.push('productName');
    }

    const isValid = missing.length === 0 && item.matchStatus !== 'CONFLICT';
    let matchStatus = item.matchStatus;
    let statusReason = item.statusReason;

    if (item.isNewProduct && matchStatus !== 'CONFLICT') {
      if (isValid) {
        matchStatus = 'NEW_VALID';
        statusReason = 'Sản phẩm mới đầy đủ thông số';
      } else {
        matchStatus = 'NEW_NEEDS_ENRICHMENT';
        statusReason = `Thiếu thông tin: ${missing.join(', ')}`;
      }
    }

    return {
      ...item,
      missingFields: missing,
      isValid,
      matchStatus,
      statusReason,
    };
  }, []);

  // Update a single item field
  const handleUpdateItemField = (id: string, field: keyof StagingItem, value: any) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const updated = { ...it, [field]: value };

        // Auto update typeName or materialName
        if (field === 'typeId') {
          const opt = productTypeOptions.find((o) => o.id === value);
          updated.typeName = opt?.label || null;
        } else if (field === 'materialId') {
          const opt = materialOptions.find((o) => o.id === value);
          updated.materialName = opt?.label || null;
        } else if (field === 'quantity' || field === 'unitPrice') {
          const qty = field === 'quantity' ? Number(value) : (it.quantity || 0);
          const price = field === 'unitPrice' ? Number(value) : (it.unitPrice || 0);
          updated.amount = qty * price;
        }

        return revalidateItem(updated);
      })
    );
  };

  // Bulk Apply to Selected Rows
  const handleApplyBulk = () => {
    if (selectedIds.size === 0) {
      toast.warning('Vui lòng chọn ít nhất 1 dòng để điền hàng loạt');
      return;
    }

    if (!bulkTypeId && !bulkMaterialId && !bulkUnit) {
      toast.warning('Vui lòng chọn ít nhất một giá trị (Loại SP, Giấy hoặc ĐVT) để áp dụng');
      return;
    }

    const typeOpt = bulkTypeId ? productTypeOptions.find((o) => o.id === bulkTypeId) : null;
    const matOpt = bulkMaterialId ? materialOptions.find((o) => o.id === bulkMaterialId) : null;

    setItems((prev) =>
      prev.map((it) => {
        if (!selectedIds.has(it.id)) return it;

        const updated = { ...it };
        if (bulkTypeId) {
          updated.typeId = bulkTypeId;
          updated.typeName = typeOpt?.label || null;
        }
        if (bulkMaterialId) {
          updated.materialId = bulkMaterialId;
          updated.materialName = matOpt?.label || null;
        }
        if (bulkUnit) {
          updated.unit = bulkUnit;
        }

        return revalidateItem(updated);
      })
    );

    toast.success(`Đã cập nhật hàng loạt cho ${selectedIds.size} dòng`);
  };

  // Selection handlers
  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      const allFilteredIds = filteredItems.map((i) => i.id);
      setSelectedIds(new Set(allFilteredIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectAllInvalid = () => {
    const invalidIds = items.filter((i) => !i.isValid).map((i) => i.id);
    setSelectedIds(new Set(invalidIds));
    setActiveTab('INVALID');
    toast.warning(`Đã chọn ${invalidIds.length} dòng cần bổ sung thông tin`);
  };

  const handleToggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Tab Filtering & Counts
  const counts = useMemo(() => {
    let matched = 0;
    let invalid = 0;
    let newValid = 0;
    let conflict = 0;

    for (const it of items) {
      if (it.matchStatus === 'MATCHED_EXACT' || it.matchStatus === 'MATCHED_NEEDS_REVIEW') matched++;
      else if (it.matchStatus === 'CONFLICT') conflict++;
      else if (!it.isValid) invalid++;
      else newValid++;
    }

    return { total: items.length, matched, invalid, newValid, conflict };
  }, [items]);

  const filteredItems = useMemo(() => {
    switch (activeTab) {
      case 'MATCHED':
        return items.filter((i) => i.matchStatus === 'MATCHED_EXACT' || i.matchStatus === 'MATCHED_NEEDS_REVIEW');
      case 'INVALID':
        return items.filter((i) => !i.isValid && i.matchStatus !== 'CONFLICT');
      case 'NEW_VALID':
        return items.filter((i) => i.isNewProduct && i.isValid && i.matchStatus !== 'CONFLICT');
      case 'CONFLICT':
        return items.filter((i) => i.matchStatus === 'CONFLICT');
      default:
        return items;
    }
  }, [items, activeTab]);

  // Submit Commit
  const handleCommit = async () => {
    if (counts.invalid > 0) {
      toast.error(`Còn ${counts.invalid} dòng sản phẩm mới chưa đủ thông tin bắt buộc. Vui lòng điền đủ trước khi lưu.`);
      return;
    }

    if (counts.conflict > 0) {
      toast.error(`Có ${counts.conflict} dòng bị xung đột mã sản phẩm. Vui lòng kiểm tra lại.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await trpcClient.products.commitImportBatch.mutate({
        items,
        overwriteMasterData,
        customerId: customerId || undefined,
        scope,
      });

      toast.success(
        `Nhập dữ liệu thành công! Đã tạo ${res.createdCount} SP mới, cập nhật ${res.updatedCount} SP.`
      );

      onSuccess({
        createdProducts: res.createdProducts,
        orderDetails: res.orderDetails,
        totalCount: items.length,
      });

      onOpenChange(false);
    } catch (err: any) {
      toast.error(`Lỗi lưu dữ liệu: ${err?.message || 'Không thể commit dữ liệu'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (scope === 'ORDER') {
      const templateCols: ExcelColumnDef[] = [
        { key: 'productCode', label: 'Mã SP', required: false },
        { key: 'productName', label: 'Tên SP', required: true },
        { key: 'specs', label: 'Quy cách', required: false },
        { key: 'unit', label: 'Đơn vị', required: true },
        { key: 'quantity', label: 'Số lượng', type: 'number', required: true },
        { key: 'unitPrice', label: 'Đơn giá', type: 'decimal', required: false },
        { key: 'poReference', label: 'Số PO', required: false },
        { key: 'soReference', label: 'Số SO', required: false },
        { key: 'customerPo', label: 'Customer PO', required: false },
        { key: 'notes', label: 'Ghi chú', required: false },
      ];
      downloadExcelTemplate(templateCols, 'mau_don_hang_chi_tiet', [
        {
          productCode: 'KH-00124',
          productName: 'Hộp Sâm Nhung Linh Chi (250x180x60mm) Ivory 300',
          specs: '250x180x60 mm, in 4 màu, cán màng mờ',
          unit: 'hộp',
          quantity: 5000,
          unitPrice: 2800,
          poReference: 'PO-9982',
          soReference: 'SO-112',
          customerPo: 'PO-KH-001',
          notes: 'Giao hàng đợt 1',
        },
      ]);
    } else {
      const templateCols: ExcelColumnDef[] = [
        { key: 'productCode', label: 'Mã SP', required: false },
        { key: 'productName', label: 'Tên SP', required: true },
        { key: 'specs', label: 'Quy cách', required: false },
        { key: 'unit', label: 'Đơn vị', required: true },
        { key: 'unitPrice', label: 'Giá bán', type: 'decimal', required: false },
        { key: 'costPrice', label: 'Giá vốn', type: 'decimal', required: false },
        { key: 'notes', label: 'Ghi chú', required: false },
      ];
      downloadExcelTemplate(templateCols, 'mau_danh_muc_san_pham', [
        {
          productCode: 'SP-00125',
          productName: 'Hộp bánh trung thu 320x220x60mm Duplex 350',
          specs: '320x220x60mm, bế gân, dán 4 góc',
          unit: 'hộp',
          unitPrice: 3500,
          costPrice: 2100,
          notes: 'Mặt hàng mùa vụ',
        },
      ]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !isSubmitting && onOpenChange(val)}>
      <DialogContent className="max-w-[96vw] w-[96vw] max-h-[94vh] h-[94vh] flex flex-col p-0 gap-0 overflow-hidden bg-slate-50">
        {/* Header */}
        <DialogHeader className="p-4 border-b bg-white flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-bold flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                <span>
                  {scope === 'ORDER'
                    ? 'Nhập & Điền Nhanh Chi Tiết Đơn Hàng'
                    : 'Nhập & Điền Nhanh Danh Mục Sản Phẩm'}
                </span>
                {fileName && (
                  <Badge variant="outline" className="text-xs font-mono font-normal">
                    {fileName}
                  </Badge>
                )}
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {customerName ? `Khách hàng: ${customerName}` : 'Dành cho khách hàng hiện tại'} · Tổng cộng{' '}
                <span className="font-semibold text-foreground">{items.length} dòng</span>
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="h-8 text-xs gap-1 border-slate-200"
              >
                <Download className="w-3.5 h-3.5 text-slate-600" />
                <span>Tải mẫu Excel</span>
              </Button>

              {/* Master Data Overwrite Toggle */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-slate-50 text-xs">
                <Switch
                  id="overwrite-mode"
                  checked={overwriteMasterData}
                  onCheckedChange={setOverwriteMasterData}
                />
                <Label htmlFor="overwrite-mode" className="cursor-pointer text-xs">
                  {overwriteMasterData ? (
                    <span className="font-semibold text-amber-600">Ghi đè thông số mới vào Master Data</span>
                  ) : (
                    <span className="text-slate-600">Tái sử dụng thông số cũ (Giữ nguyên Master Data)</span>
                  )}
                </Label>
              </div>
            </div>
          </div>

          {/* Filter Tabs & Quick Action */}
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t">
            <div className="flex items-center gap-1.5 overflow-x-auto text-xs">
              <button
                type="button"
                onClick={() => setActiveTab('ALL')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5',
                  activeTab === 'ALL'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border'
                )}
              >
                <span>Tất cả</span>
                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">{counts.total}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('INVALID')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 border',
                  activeTab === 'INVALID'
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-amber-700 hover:bg-amber-50 border-amber-200'
                )}
              >
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Cần bổ sung</span>
                <span
                  className={cn(
                    'px-1.5 py-0.2 rounded-full text-[10px] font-bold',
                    counts.invalid > 0 ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700'
                  )}
                >
                  {counts.invalid}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('MATCHED')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 border',
                  activeTab === 'MATCHED'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-emerald-700 hover:bg-emerald-50 border-emerald-200'
                )}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Đã khớp</span>
                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-emerald-100 text-emerald-900 font-bold">
                  {counts.matched}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('NEW_VALID')}
                className={cn(
                  'px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 border',
                  activeTab === 'NEW_VALID'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-blue-700 hover:bg-blue-50 border-blue-200'
                )}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>SP mới hợp lệ</span>
                <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-blue-100 text-blue-900 font-bold">
                  {counts.newValid}
                </span>
              </button>

              {counts.conflict > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveTab('CONFLICT')}
                  className={cn(
                    'px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5 border',
                    activeTab === 'CONFLICT'
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-purple-700 hover:bg-purple-50 border-purple-200'
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5" />
                  <span>Xung đột mã ({counts.conflict})</span>
                </button>
              )}
            </div>

            {counts.invalid > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSelectAllInvalid}
                className="text-xs h-7 text-amber-700 border-amber-300 hover:bg-amber-50"
              >
                <Zap className="w-3.5 h-3.5 mr-1 text-amber-600" />
                Chọn tất cả {counts.invalid} dòng cần bổ sung
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Bulk Fill Down Bar */}
        <div className="bg-indigo-50/70 border-b border-indigo-100 px-4 py-2 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-indigo-900 flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-indigo-600" />
              <span>Điền hàng loạt ({selectedIds.size} dòng chọn):</span>
            </span>

            {/* Select Loại SP */}
            <select
              value={bulkTypeId}
              onChange={(e) => setBulkTypeId(e.target.value)}
              className="h-7 text-xs rounded border border-indigo-200 bg-white px-2 py-0.5 text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">-- Chọn Loại SP --</option>
              {productTypeOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Select Giấy */}
            <select
              value={bulkMaterialId}
              onChange={(e) => setBulkMaterialId(e.target.value)}
              className="h-7 text-xs rounded border border-indigo-200 bg-white px-2 py-0.5 text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 max-w-[200px]"
            >
              <option value="">-- Chọn Giấy Chính --</option>
              {materialOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Select ĐVT */}
            <select
              value={bulkUnit}
              onChange={(e) => setBulkUnit(e.target.value)}
              className="h-7 text-xs rounded border border-indigo-200 bg-white px-2 py-0.5 text-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-24"
            >
              <option value="">-- ĐVT --</option>
              {combinedUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>

            <Button
              type="button"
              size="sm"
              onClick={handleApplyBulk}
              disabled={selectedIds.size === 0}
              className="h-7 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
            >
              ⚡ Áp dụng cho dòng chọn
            </Button>
          </div>

          {selectedIds.size > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              className="h-7 text-xs text-slate-600 hover:text-slate-900"
            >
              Bỏ chọn ({selectedIds.size})
            </Button>
          )}
        </div>

        {/* Data Table */}
        <div className="flex-1 overflow-auto relative bg-white">
          {isInspecting ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm font-medium text-slate-600">Đang đối soát sản phẩm và phân tích kích thước...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 py-16 text-slate-500">
              <Package className="w-8 h-8 text-slate-300" />
              <p className="text-sm">Không có dòng nào trong bộ lọc này</p>
            </div>
          ) : (
            <table className="w-full text-xs text-left border-collapse">
              <thead className="sticky top-0 z-30 bg-slate-100 border-b shadow-sm font-semibold text-slate-700">
                <tr>
                  <th className="w-10 px-2 py-2 text-center sticky left-0 z-40 bg-slate-100 border-r !bg-slate-100">
                    <Checkbox
                      checked={
                        filteredItems.length > 0 &&
                        filteredItems.every((i) => selectedIds.has(i.id))
                      }
                      onCheckedChange={(c) => handleToggleSelectAll(!!c)}
                    />
                  </th>
                  <th className="w-12 px-2 py-2 text-center border-r">STT</th>
                  <th className="w-32 px-2 py-2 border-r">Trạng thái</th>
                  <th className="w-36 px-2 py-2 border-r sticky left-10 z-40 bg-slate-100 !bg-slate-100">
                    Mã Sản Phẩm
                  </th>
                  <th className="min-w-[220px] px-2 py-2 border-r sticky left-[184px] z-40 bg-slate-100 !bg-slate-100">
                    Tên Sản Phẩm *
                  </th>
                  <th className="w-40 px-2 py-2 border-r">Loại SP *</th>
                  <th className="w-48 px-2 py-2 border-r">Giấy Chính *</th>
                  <th className="w-16 px-1.5 py-2 text-center border-r">Dài (mm) *</th>
                  <th className="w-16 px-1.5 py-2 text-center border-r">Rộng (mm) *</th>
                  <th className="w-16 px-1.5 py-2 text-center border-r">Cao (mm)</th>
                  <th className="w-20 px-2 py-2 text-center border-r">ĐVT *</th>

                  {scope === 'ORDER' && (
                    <>
                      <th className="w-24 px-2 py-2 text-right border-r">Số Lượng</th>
                      <th className="w-24 px-2 py-2 text-right border-r">Đơn Giá</th>
                      <th className="w-28 px-2 py-2 text-right border-r">Thành Tiền</th>
                      <th className="w-28 px-2 py-2 border-r">Số PO</th>
                      <th className="w-28 px-2 py-2 border-r">Số SO</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  const isInvalid = !item.isValid;

                  return (
                    <tr
                      key={item.id}
                      className={cn(
                        'transition-colors hover:bg-slate-50/80',
                        isSelected && 'bg-indigo-50/40',
                        isInvalid && !isSelected && 'bg-amber-50/20'
                      )}
                    >
                      {/* Checkbox */}
                      <td className="px-2 py-1.5 text-center sticky left-0 z-20 bg-white border-r !bg-white">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleSelectRow(item.id)}
                        />
                      </td>

                      {/* STT */}
                      <td className="px-2 py-1.5 text-center text-slate-400 font-mono border-r">
                        {item.rowNumber}
                      </td>

                      {/* Trạng thái */}
                      <td className="px-2 py-1.5 border-r">
                        {item.matchStatus === 'MATCHED_EXACT' && (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-300 text-[10px] py-0">
                            🟢 Đã khớp DB
                          </Badge>
                        )}
                        {item.matchStatus === 'MATCHED_NEEDS_REVIEW' && (
                          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-300 text-[10px] py-0">
                            🔵 Khớp tên
                          </Badge>
                        )}
                        {item.matchStatus === 'NEW_VALID' && (
                          <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 border-indigo-300 text-[10px] py-0">
                            ✨ SP mới hợp lệ
                          </Badge>
                        )}
                        {item.matchStatus === 'NEW_NEEDS_ENRICHMENT' && (
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300 text-[10px] py-0 animate-pulse">
                            🟡 Cần bổ sung
                          </Badge>
                        )}
                        {item.matchStatus === 'CONFLICT' && (
                          <Badge className="bg-purple-100 text-purple-900 hover:bg-purple-100 border-purple-300 text-[10px] py-0">
                            🟠 Xung đột mã
                          </Badge>
                        )}
                      </td>

                      {/* Mã SP */}
                      <td className="px-2 py-1.5 border-r sticky left-10 z-20 bg-white border-r !bg-white">
                        <div className="flex items-center gap-1">
                          <Input
                            value={item.productCode}
                            onChange={(e) => handleUpdateItemField(item.id, 'productCode', e.target.value)}
                            className="h-6 text-xs font-mono px-1.5 py-0"
                          />
                          {item.isAutoGeneratedCode && (
                            <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded flex-shrink-0 font-sans">
                              Tự sinh
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Tên SP */}
                      <td className="px-2 py-1.5 border-r sticky left-[184px] z-20 bg-white border-r !bg-white">
                        <Input
                          value={item.productName}
                          onChange={(e) => handleUpdateItemField(item.id, 'productName', e.target.value)}
                          className={cn(
                            'h-6 text-xs px-1.5 py-0',
                            !item.productName?.trim() && 'border-red-400 bg-red-50'
                          )}
                        />
                      </td>

                      {/* Loại SP */}
                      <td
                        className={cn(
                          'px-1.5 py-1 border-r',
                          item.isNewProduct && !item.typeId && 'bg-amber-50/80 border-amber-300'
                        )}
                      >
                        <select
                          value={item.typeId || ''}
                          onChange={(e) => handleUpdateItemField(item.id, 'typeId', e.target.value)}
                          className={cn(
                            'w-full h-6 text-xs rounded border px-1 py-0 bg-white',
                            item.isNewProduct && !item.typeId
                              ? 'border-amber-400 text-amber-900 font-medium'
                              : 'border-slate-200'
                          )}
                        >
                          <option value="">-- Chọn Loại SP --</option>
                          {productTypeOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Giấy chính */}
                      <td
                        className={cn(
                          'px-1.5 py-1 border-r',
                          item.isNewProduct && !item.materialId && 'bg-amber-50/80 border-amber-300'
                        )}
                      >
                        <select
                          value={item.materialId || ''}
                          onChange={(e) => handleUpdateItemField(item.id, 'materialId', e.target.value)}
                          className={cn(
                            'w-full h-6 text-xs rounded border px-1 py-0 bg-white max-w-[200px]',
                            item.isNewProduct && !item.materialId
                              ? 'border-amber-400 text-amber-900 font-medium'
                              : 'border-slate-200'
                          )}
                        >
                          <option value="">-- Chọn Giấy --</option>
                          {materialOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Dài */}
                      <td
                        className={cn(
                          'px-1 py-1 text-center border-r',
                          item.isNewProduct && !item.length && !item.flatLength && 'bg-amber-50/80'
                        )}
                      >
                        <Input
                          type="number"
                          value={item.length ?? ''}
                          placeholder={item.flatLength ? String(item.flatLength) : '0'}
                          onChange={(e) =>
                            handleUpdateItemField(item.id, 'length', e.target.value ? Number(e.target.value) : null)
                          }
                          className={cn(
                            'h-6 text-xs text-center px-1 py-0 w-14',
                            item.isNewProduct && !item.length && !item.flatLength && 'border-amber-400 bg-amber-50'
                          )}
                        />
                      </td>

                      {/* Rộng */}
                      <td
                        className={cn(
                          'px-1 py-1 text-center border-r',
                          item.isNewProduct && !item.width && !item.flatWidth && 'bg-amber-50/80'
                        )}
                      >
                        <Input
                          type="number"
                          value={item.width ?? ''}
                          placeholder={item.flatWidth ? String(item.flatWidth) : '0'}
                          onChange={(e) =>
                            handleUpdateItemField(item.id, 'width', e.target.value ? Number(e.target.value) : null)
                          }
                          className={cn(
                            'h-6 text-xs text-center px-1 py-0 w-14',
                            item.isNewProduct && !item.width && !item.flatWidth && 'border-amber-400 bg-amber-50'
                          )}
                        />
                      </td>

                      {/* Cao */}
                      <td className="px-1 py-1 text-center border-r">
                        <Input
                          type="number"
                          value={item.height ?? ''}
                          placeholder="0"
                          onChange={(e) =>
                            handleUpdateItemField(item.id, 'height', e.target.value ? Number(e.target.value) : null)
                          }
                          className="h-6 text-xs text-center px-1 py-0 w-14"
                        />
                      </td>

                      {/* ĐVT */}
                      <td className="px-1.5 py-1 text-center border-r">
                        <select
                          value={item.unit}
                          onChange={(e) => handleUpdateItemField(item.id, 'unit', e.target.value)}
                          className="w-full h-6 text-xs rounded border border-slate-200 px-1 py-0 bg-white"
                        >
                          {combinedUnits.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Cột giao dịch khi scope === ORDER */}
                      {scope === 'ORDER' && (
                        <>
                          <td className="px-1.5 py-1 text-right border-r">
                            <Input
                              type="number"
                              value={item.quantity ?? ''}
                              onChange={(e) =>
                                handleUpdateItemField(item.id, 'quantity', e.target.value ? Number(e.target.value) : 0)
                              }
                              className="h-6 text-xs text-right px-1 py-0 w-20 ml-auto font-mono"
                            />
                          </td>

                          <td className="px-1.5 py-1 text-right border-r">
                            <Input
                              type="number"
                              value={item.unitPrice ?? ''}
                              onChange={(e) =>
                                handleUpdateItemField(item.id, 'unitPrice', e.target.value ? Number(e.target.value) : 0)
                              }
                              className="h-6 text-xs text-right px-1 py-0 w-20 ml-auto font-mono"
                            />
                          </td>

                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-800 border-r">
                            {(item.amount || 0).toLocaleString()}
                          </td>

                          <td className="px-1.5 py-1 border-r">
                            <Input
                              value={item.poReference || ''}
                              placeholder="Số PO..."
                              onChange={(e) => handleUpdateItemField(item.id, 'poReference', e.target.value)}
                              className="h-6 text-xs px-1.5 py-0"
                            />
                          </td>

                          <td className="px-1.5 py-1 border-r">
                            <Input
                              value={item.soReference || ''}
                              placeholder="Số SO..."
                              onChange={(e) => handleUpdateItemField(item.id, 'soReference', e.target.value)}
                              className="h-6 text-xs px-1.5 py-0"
                            />
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-3 border-t bg-white flex-shrink-0 flex items-center justify-between sm:justify-between">
          {/* Warning / status text */}
          <div className="flex items-center gap-2 text-xs">
            {counts.invalid > 0 ? (
              <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded border border-amber-200 font-medium animate-pulse">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span>
                  Còn <strong>{counts.invalid} dòng</strong> sản phẩm mới chưa đủ thông tin bắt buộc (Loại SP / Giấy / Kích thước).
                </span>
              </div>
            ) : counts.conflict > 0 ? (
              <div className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-2.5 py-1 rounded border border-purple-200 font-medium">
                <AlertCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span>Có {counts.conflict} dòng xung đột mã sản phẩm.</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200 font-medium">
                <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span>Tất cả {items.length} dòng đều hợp lệ và sẵn sàng lưu.</span>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Hủy bỏ
            </Button>

            <Button
              type="button"
              size="sm"
              onClick={handleCommit}
              disabled={counts.invalid > 0 || counts.conflict > 0 || items.length === 0 || isSubmitting}
              className={cn(
                'bg-indigo-600 hover:bg-indigo-700 text-white font-medium',
                (counts.invalid > 0 || counts.conflict > 0) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Đang lưu...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-1.5" />
                  {scope === 'ORDER'
                    ? `Lưu & Đưa Vào Đơn Hàng (${items.length} dòng)`
                    : `Lưu Vào Danh Mục Sản Phẩm (${items.length} SP)`}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
